import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Percent, Clock, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { promotionsApi, Promotion } from '../services/api';
import SEO from '../components/SEO';
import { stripHtmlSafe, sanitizeHtml } from '../utils/sanitize';

const Promosyonlar: React.FC = () => {
  const { t } = useTranslation();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPromo, setSelectedPromo] = useState<Promotion | null>(null);

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        const result = await promotionsApi.list();
        if (result.success && result.data) {
          setPromotions(result.data.promotions);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadPromotions();
  }, []);

  return (
    <>
      <SEO
        title={t('games:promotions.seoTitle')}
        description={t('games:promotions.seoDescription')}
        url="/etkinlikler/promosyonlar"
      />
      <div className="min-h-screen pt-24 pb-12 relative overflow-hidden">

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <Gift className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('games:promotions.title')}
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('games:promotions.description')}
            </p>
          </motion.div>

          {/* Loading State */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-dark-800/50 rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-dark-700/50" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-dark-700/50 rounded w-3/4" />
                    <div className="h-4 bg-dark-700/50 rounded w-full" />
                    <div className="h-4 bg-dark-700/50 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : promotions.length === 0 ? (
            /* Empty State */
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-12 border border-dark-700 text-center max-w-lg mx-auto"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-dark-700/50 flex items-center justify-center">
                <Percent className="w-10 h-10 text-gray-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">{t('games:promotions.empty.title')}</h2>
              <p className="text-gray-400">
                {t('games:promotions.empty.description')}
              </p>
            </motion.div>
          ) : (
            /* Promotions Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {promotions.map((promo, index) => (
                <motion.div
                  key={promo.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group bg-dark-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-dark-700 hover:border-pink-500/50 transition-all duration-300 cursor-pointer flex flex-col"
                  onClick={() => setSelectedPromo(promo)}
                >
                  {/* Image */}
                  {promo.imageUrl && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={promo.imageUrl}
                        alt={promo.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/20 to-transparent" />

                      {/* Type Badge */}
                      {promo.isFeatured && (
                        <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-pink-500/90 backdrop-blur-sm text-white text-sm font-semibold">
                          {t('games:promotions.featured')}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-pink-400 transition-colors line-clamp-2">
                      {promo.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed line-clamp-3 flex-1">
                      {promo.description ? stripHtmlSafe(promo.description) : promo.shortDescription || ''}
                    </p>

                    {/* Action */}
                    <div className="mt-4 pt-4 border-t border-dark-700/50 flex items-center justify-between">
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {t('games:promotions.active')}
                      </span>
                      <span className="text-pink-400 text-sm font-medium flex items-center group-hover:translate-x-1 transition-transform">
                        {t('games:promotions.details')}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedPromo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPromo(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-dark-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-dark-700"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedPromo.imageUrl && (
              <div className="relative h-64">
                <img
                  src={selectedPromo.imageUrl}
                  alt={selectedPromo.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-800 to-transparent" />
              </div>
            )}
            <div className="p-6 -mt-16 relative">
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-white">{selectedPromo.title}</h2>
              </div>
              {selectedPromo.description && (
                <div
                  className="prose prose-invert max-w-none text-gray-300 leading-relaxed overflow-y-auto max-h-[50vh]"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedPromo.description) }}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Promosyonlar;
